/**
 * The active tenant is the row-level scope for every API call: the app sends it
 * as `X-Tenant-Id` and the API resolves the caller's role within it (ADR-0016).
 *
 * It is persisted in localStorage so a refresh keeps the same tenant, and read
 * synchronously by the http-client on every request. This is a per-browser
 * convenience, NOT the source of truth — the API always re-validates that the
 * caller is a member of the tenant it's given, and ignores/《rejects》a tenant
 * the user can't access.
 */
const STORAGE_KEY = "ge.activeTenantId";

/**
 * Decide the active tenant for the session. Precedence, highest first:
 *   1. the tenant the API explicitly resolved (from the X-Tenant-Id we sent),
 *   2. whatever we already had stored (keep the user's current scope),
 *   3. the first tenant the caller can access (bootstrap on the very first load,
 *      e.g. a superadmin with no membership whose /auth/me returns activeTenantId
 *      = null but a non-empty `tenants` list).
 * Returns null only when there is genuinely no tenant to act within.
 *
 * Pure by design so the bootstrap rule is unit-tested without mocking Firebase.
 */
export function resolveActiveTenant(
  apiActiveTenantId: string | null,
  stored: string | null,
  accessibleTenantIds: string[],
): string | null {
  return apiActiveTenantId ?? stored ?? accessibleTenantIds[0] ?? null;
}

let current: string | null = read();

function read(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode / storage disabled — fall back to in-memory only.
    return null;
  }
}

export function getActiveTenantId(): string | null {
  return current;
}

export function setActiveTenantId(tenantId: string | null): void {
  current = tenantId;
  try {
    if (tenantId) {
      window.localStorage.setItem(STORAGE_KEY, tenantId);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // In-memory `current` still reflects the change.
  }
}
